// engine2025/params/minimums.ts — F9 (§A.10): state and regional personal/family minimum
// amounts (arts. 57-61 LIRPF; art. 46.1.a Ley 22/2009 for the ±10% regional competence).
// Source: docs/guia-renta-2025/14-minimo-personal-familiar.md (Cap.14, Manual pp. 1067-1115).
// The former ⚠️VERIFICAR items (Illes Balears 1st descendant, Cataluña) are resolved in
// docs/guia-renta-2025/parametros-verificados.md: IB keeps the state 2.400 € for the 1st
// descendant, and Cataluña has NO differentiated regional minimums (uses the state ones).

import { Cents, parseEuros } from '../money';
import { CCAA } from '../types';
import { ParamRegistry } from './registry';

/** One complete set of minimum amounts (state, or one CCAA's regional override). */
export interface MinimumAmounts {
    taxpayerBase: Cents;
    /** Alternative base when any taxpayer of the declaration is ≥65 (only Illes Balears). */
    taxpayerBaseOver65?: Cents;
    taxpayerOver65: Cents;
    taxpayerOver75: Cents;
    /** 1st, 2nd, 3rd, 4th-and-following descendant. */
    descendantByOrder: [Cents, Cents, Cents, Cents];
    descendantUnder3: Cents;
    descendantDeceased: Cents;
    ascendant: Cents;
    ascendantOver75: Cents;
    disability33: Cents;
    disability65: Cents;
    disabilityAssistance: Cents;
    /** La Rioja overrides disability amounts ONLY for descendants (Cap.14 §4). */
    descendantDisability33?: Cents;
    descendantDisability65?: Cents;
}

/** Registers all Cap.14 minimum params (state P-130..P-143 and regional P-NEW-17-x). */
export function registerMinimumParams(params: ParamRegistry): void {
    const src = (page: string) => ({
        chapter: 'Cap.14 — 14-minimo-personal-familiar.md',
        page,
    });

    params.define('P-130', parseEuros('5.550,00'), { concept: 'Mínimo del contribuyente, general', verified: true, source: src('p. 1068') });
    params.define('P-131', parseEuros('1.150,00'), { concept: 'Incremento contribuyente >65 años', verified: true, source: src('p. 1068') });
    params.define('P-132', parseEuros('1.400,00'), { concept: 'Incremento adicional contribuyente >75 años', verified: true, source: src('p. 1068') });
    params.define('P-133', parseEuros('2.400,00'), { concept: 'Mínimo por 1er descendiente', verified: true, source: src('p. 1073') });
    params.define('P-134', parseEuros('2.700,00'), { concept: 'Mínimo por 2º descendiente', verified: true, source: src('p. 1073') });
    params.define('P-135', parseEuros('4.000,00'), { concept: 'Mínimo por 3er descendiente', verified: true, source: src('p. 1073') });
    params.define('P-136', parseEuros('4.500,00'), { concept: 'Mínimo por 4º descendiente y siguientes', verified: true, source: src('p. 1073') });
    params.define('P-137', parseEuros('2.800,00'), { concept: 'Incremento por descendiente menor de 3 años (o ventana de adopción/acogimiento)', verified: true, source: src('p. 1074') });
    params.define('P-138', parseEuros('2.400,00'), { concept: 'Mínimo por descendiente fallecido en el año (importe fijo)', verified: true, source: src('p. 1073') });
    params.define('P-139', parseEuros('1.150,00'), { concept: 'Mínimo por ascendiente >65 años (o con discapacidad, cualquier edad)', verified: true, source: src('p. 1077') });
    params.define('P-140', parseEuros('1.400,00'), { concept: 'Incremento adicional ascendiente >75 años', verified: true, source: src('p. 1077') });
    params.define('P-141', parseEuros('3.000,00'), { concept: 'Mínimo por discapacidad 33-64% (contribuyente/ascendiente/descendiente)', verified: true, source: src('p. 1078') });
    params.define('P-142', parseEuros('9.000,00'), { concept: 'Mínimo por discapacidad ≥65%', verified: true, source: src('p. 1078') });
    params.define('P-143', parseEuros('3.000,00'), { concept: 'Incremento por gastos de asistencia (ayuda de terceros/movilidad reducida o discapacidad ≥65%)', verified: true, source: src('p. 1078') });

    // Regional overrides (only CCAA that exercised the competence; the rest use state amounts).
    const cents = parseEuros;
    params.define(
        'P-NEW-17-1',
        {
            taxpayerBase: cents('5.790,00'), taxpayerOver65: cents('1.200,00'), taxpayerOver75: cents('1.460,00'),
            descendantByOrder: [cents('2.510,00'), cents('2.820,00'), cents('4.170,00'), cents('4.700,00')],
            descendantUnder3: cents('2.920,00'), descendantDeceased: cents('2.400,00'),
            ascendant: cents('1.200,00'), ascendantOver75: cents('1.460,00'),
            disability33: cents('3.130,00'), disability65: cents('9.390,00'), disabilityAssistance: cents('3.130,00'),
        } satisfies MinimumAmounts,
        { concept: 'Mínimos autonómicos 2025 — Andalucía', verified: true, source: src('p. 1083-1084') }
    );
    params.define(
        'P-NEW-17-2',
        {
            taxpayerBase: cents('6.105,00'), taxpayerOver65: cents('1.265,00'), taxpayerOver75: cents('1.540,00'),
            descendantByOrder: [cents('2.640,00'), cents('2.970,00'), cents('4.400,00'), cents('4.950,00')],
            descendantUnder3: cents('3.080,00'), descendantDeceased: cents('2.400,00'),
            ascendant: cents('1.265,00'), ascendantOver75: cents('1.540,00'),
            disability33: cents('3.300,00'), disability65: cents('9.900,00'), disabilityAssistance: cents('3.300,00'),
        } satisfies MinimumAmounts,
        { concept: 'Mínimos autonómicos 2025 — Principado de Asturias', verified: true, source: src('p. 1084-1085') }
    );
    params.define(
        'P-NEW-17-3',
        {
            // 1er descendiente = 2.400 € (estatal): VERIFICADO en parametros-verificados.md
            // ("Illes Balears — Mínimos por descendiente"); el ejemplo 5 del capítulo lo confirma.
            taxpayerBase: cents('5.550,00'), taxpayerBaseOver65: cents('6.105,00'),
            taxpayerOver65: cents('1.265,00'), taxpayerOver75: cents('1.540,00'),
            descendantByOrder: [cents('2.400,00'), cents('2.970,00'), cents('4.400,00'), cents('4.950,00')],
            descendantUnder3: cents('2.800,00'), descendantDeceased: cents('2.400,00'),
            ascendant: cents('1.265,00'), ascendantOver75: cents('1.540,00'),
            disability33: cents('3.300,00'), disability65: cents('9.900,00'), disabilityAssistance: cents('3.300,00'),
        } satisfies MinimumAmounts,
        { concept: 'Mínimos autonómicos 2025 — Illes Balears', verified: true, source: src('p. 1085-1086 + parametros-verificados.md') }
    );
    params.define(
        'P-NEW-17-4',
        {
            taxpayerBase: cents('5.606,00'), taxpayerOver65: cents('1.162,00'), taxpayerOver75: cents('1.414,00'),
            descendantByOrder: [cents('2.424,00'), cents('2.727,00'), cents('4.040,00'), cents('4.545,00')],
            descendantUnder3: cents('2.828,00'), descendantDeceased: cents('2.400,00'),
            ascendant: cents('1.162,00'), ascendantOver75: cents('1.414,00'),
            disability33: cents('3.030,00'), disability65: cents('9.090,00'), disabilityAssistance: cents('3.030,00'),
        } satisfies MinimumAmounts,
        { concept: 'Mínimos autonómicos 2025 — Canarias', verified: true, source: src('p. 1086-1087') }
    );
    params.define(
        'P-NEW-17-5',
        {
            taxpayerBase: cents('5.789,00'), taxpayerOver65: cents('1.199,00'), taxpayerOver75: cents('1.460,00'),
            descendantByOrder: [cents('2.503,00'), cents('2.816,00'), cents('4.172,00'), cents('4.694,00')],
            descendantUnder3: cents('2.920,00'), descendantDeceased: cents('2.400,00'),
            ascendant: cents('1.199,00'), ascendantOver75: cents('1.460,00'),
            disability33: cents('3.129,00'), disability65: cents('9.387,00'), disabilityAssistance: cents('3.129,00'),
        } satisfies MinimumAmounts,
        { concept: 'Mínimos autonómicos 2025 — Galicia', verified: true, source: src('p. 1088-1089') }
    );
    params.define(
        'P-NEW-17-6',
        {
            taxpayerBase: cents('5.956,65'), taxpayerOver65: cents('1.234,26'), taxpayerOver75: cents('1.502,58'),
            descendantByOrder: [cents('2.575,85'), cents('2.897,83'), cents('4.400,00'), cents('4.950,00')],
            descendantUnder3: cents('3.005,16'), descendantDeceased: cents('2.400,00'),
            ascendant: cents('1.234,26'), ascendantOver75: cents('1.502,58'),
            disability33: cents('3.219,81'), disability65: cents('9.659,44'), disabilityAssistance: cents('3.219,81'),
        } satisfies MinimumAmounts,
        { concept: 'Mínimos autonómicos 2025 — Comunidad de Madrid', verified: true, source: src('p. 1089-1090') }
    );
    params.define(
        'P-NEW-17-7',
        {
            // La Rioja solo modifica la discapacidad de DESCENDIENTES (+10%); el resto es estatal.
            taxpayerBase: cents('5.550,00'), taxpayerOver65: cents('1.150,00'), taxpayerOver75: cents('1.400,00'),
            descendantByOrder: [cents('2.400,00'), cents('2.700,00'), cents('4.000,00'), cents('4.500,00')],
            descendantUnder3: cents('2.800,00'), descendantDeceased: cents('2.400,00'),
            ascendant: cents('1.150,00'), ascendantOver75: cents('1.400,00'),
            disability33: cents('3.000,00'), disability65: cents('9.000,00'), disabilityAssistance: cents('3.000,00'),
            descendantDisability33: cents('3.300,00'), descendantDisability65: cents('9.900,00'),
        } satisfies MinimumAmounts,
        { concept: 'Mínimos autonómicos 2025 — La Rioja (solo discapacidad de descendientes)', verified: true, source: src('p. 1090-1091') }
    );
    params.define(
        'P-NEW-17-8',
        {
            taxpayerBase: cents('6.105,00'), taxpayerOver65: cents('1.265,00'), taxpayerOver75: cents('1.540,00'),
            descendantByOrder: [cents('2.640,00'), cents('2.970,00'), cents('4.400,00'), cents('4.950,00')],
            descendantUnder3: cents('3.080,00'), descendantDeceased: cents('2.400,00'),
            ascendant: cents('1.265,00'), ascendantOver75: cents('1.540,00'),
            disability33: cents('3.300,00'), disability65: cents('9.900,00'), disabilityAssistance: cents('3.300,00'),
        } satisfies MinimumAmounts,
        { concept: 'Mínimos autonómicos 2025 — Comunitat Valenciana', verified: true, source: src('p. 1091-1092') }
    );
}

/** State minimum amounts, assembled from P-130..P-143. */
export function getStateMinimumAmounts(params: ParamRegistry): MinimumAmounts {
    return {
        taxpayerBase: params.get<Cents>('P-130'),
        taxpayerOver65: params.get<Cents>('P-131'),
        taxpayerOver75: params.get<Cents>('P-132'),
        descendantByOrder: [
            params.get<Cents>('P-133'),
            params.get<Cents>('P-134'),
            params.get<Cents>('P-135'),
            params.get<Cents>('P-136'),
        ],
        descendantUnder3: params.get<Cents>('P-137'),
        descendantDeceased: params.get<Cents>('P-138'),
        ascendant: params.get<Cents>('P-139'),
        ascendantOver75: params.get<Cents>('P-140'),
        disability33: params.get<Cents>('P-141'),
        disability65: params.get<Cents>('P-142'),
        disabilityAssistance: params.get<Cents>('P-143'),
    };
}

/** CCAA → override param id; absent = the CCAA did not exercise the competence (state amounts). */
const REGIONAL_OVERRIDE_PARAM: Partial<Record<CCAA, string>> = {
    AN: 'P-NEW-17-1',
    AS: 'P-NEW-17-2',
    IB: 'P-NEW-17-3',
    CN: 'P-NEW-17-4',
    GA: 'P-NEW-17-5',
    MD: 'P-NEW-17-6',
    RI: 'P-NEW-17-7',
    VC: 'P-NEW-17-8',
    // CL y CT: iguales a los estatales (Cap.14 §4 + parametros-verificados.md — Cataluña).
    // AR, CB, CM, EX, MC, CE, ML: sin ejercicio de la competencia → estatal.
};

/** Regional minimum amounts for the autonomic levy (art. 46.1.a Ley 22/2009). */
export function getRegionalMinimumAmounts(ccaa: CCAA, params: ParamRegistry): MinimumAmounts {
    if (ccaa === 'OUT_OF_SCOPE') {
        throw new Error('Mínimos autonómicos: País Vasco/Navarra (forales) fuera de alcance del motor');
    }
    const paramId = REGIONAL_OVERRIDE_PARAM[ccaa];
    return paramId ? params.get<MinimumAmounts>(paramId) : getStateMinimumAmounts(params);
}
